import { useServerRequest } from '../../api'
import { basicS3Upload } from '../../basicS3Upload'
import { type GetHookResponse } from '../../hooks'
import { gamePrizeUploadKey } from '../../keys'
import { useTantalimQuery } from '../../query'
import { usePrizeCache } from './cache'
import { type GamePrizeUploadInput, type GamePrizeUploadSuccess, type GameId, type PrizeId } from '@terros/common'
import { useCallback, useState } from 'react'

export function usePrizeUploadData(gameId: GameId, prizeId?: PrizeId): GetHookResponse<GamePrizeUploadSuccess> {
  return useTantalimQuery<GamePrizeUploadInput, GamePrizeUploadSuccess, GamePrizeUploadSuccess>({
    url: '/game/prize/upload',
    input: { gameId, prizeId },
    convertResponse: (data) => data,
    queryKey: gamePrizeUploadKey(gameId, prizeId),
  })
}

type PrizeUploadResponse = {
  downloadUrl?: string
  prizeId?: PrizeId
  isUploading: boolean
  isLoading: boolean
  error?: string
  upload: (file: File | Blob | null) => Promise<void>
  clear: () => void
}

export function useUploadPrize(gameId: GameId, prizeId?: PrizeId): PrizeUploadResponse {
  const { data, isLoading, error } = usePrizeUploadData(gameId, prizeId)

  const cache = usePrizeCache()
  const [isUploading, setIsUploading] = useState<boolean>(false)

  const clear = (): void => {
    cache.clearItem(gameId, prizeId)
  }

  const upload = async (file: File | Blob | null): Promise<void> => {
    if (!file) return
    try {
      setIsUploading(true)
      await basicS3Upload(data?.upload, file)
      await new Promise((resolve) => setTimeout(resolve, 3000))
    } finally {
      setIsUploading(false)
    }
  }

  return {
    downloadUrl: data?.download,
    prizeId: prizeId ?? data?.prizeId,
    isUploading,
    isLoading,
    error,
    upload,
    clear,
  }
}

export const useGetPrizeUpload = (): ((gameId: GameId, prizeId?: PrizeId) => Promise<GamePrizeUploadSuccess>) => {
  const serverRequest = useServerRequest<GamePrizeUploadInput, GamePrizeUploadSuccess>('/game/prize/upload')
  return useCallback(
    async (gameId: GameId, prizeId?: PrizeId) => {
      return serverRequest({
        gameId,
        prizeId,
      })
    },
    [serverRequest]
  )
}
